/**
 * The QR dialog: create a pairing, show its code, watch the server's answer.
 *
 * Three hazards shaped this file, and two of them have bitten this repository
 * before.
 *
 * **A rerender must not create a second pairing.** Creation is guarded by a
 * ref rather than by an effect dependency list, because a dependency array is
 * one careless object literal away from firing again - and firing again here
 * means a second live credential nobody is watching. Clicking "Yeni Kod" is
 * the only other way to create one.
 *
 * **Polling must stop.** It stops on every terminal state and on unmount. An
 * interval that outlives its dialog is a request loop that runs until the tab
 * closes.
 *
 * **The server owns the state.** The countdown is decoration; when it reaches
 * zero the component asks the server rather than deciding for itself that the
 * code expired. A second desktop tab watching the same pairing converges on
 * the same answer because both are reading the same row.
 *
 * The secret lives in `pairing.pairing_url` for as long as the dialog is open,
 * is rendered only into a QR, and is never logged, stored, or shown as text.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { createPhonePairing, getPhonePairing, revokePhonePairing } from "@/api/client";
import type { PairingCreated, PairingState, PairingStatus } from "@/api/types";
import { Button } from "@/components/ui";
import { PAIRING_TERMS } from "@/i18n/terms";
import "./pairing.css";
import { useDialogFocus } from "@/lib/useDialogFocus";

/** Slow enough not to hammer a local API, fast enough to feel immediate. */
const POLL_INTERVAL_MS = 2000;

const TERMINAL: PairingStatus[] = ["connected", "expired", "revoked"];

function secondsLeft(expiresAt: string, now: number): number {
  return Math.max(0, Math.floor((Date.parse(expiresAt) - now) / 1000));
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

interface Props {
  projectId: string;
  captureSessionId?: string | undefined;
  onClose: () => void;
}

export function PhonePairingDialog({ projectId, captureSessionId, onClose }: Props) {
  const [pairing, setPairing] = useState<PairingCreated | null>(null);
  const [status, setStatus] = useState<PairingStatus | null>(null);
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  // How long this QR should stay redeemable. A product choice, not a security
  // control - the security is the one-time secret - so the person creating
  // the connection picks it, and changing it mints a fresh code.
  const [ttlSeconds, setTtlSeconds] = useState(300);

  // Guards creation against every rerender, including StrictMode's double
  // invoke in development. Without it a mount can mint two credentials and
  // leave one of them live and unobserved.
  const creating = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const create = useCallback(async () => {
    if (creating.current) return;
    creating.current = true;
    setFailed(false);
    setPairing(null);
    setStatus(null);
    setDeviceLabel(null);
    try {
      const created = await createPhonePairing(
        projectId,
        {
          ...(captureSessionId ? { capture_session_id: captureSessionId } : {}),
          ttl_seconds: ttlSeconds,
        },
      );
      setPairing(created);
      setStatus(created.status);
    } catch {
      // Nothing from the error is shown or logged: a pairing failure message
      // is one of the few places a credential could plausibly appear.
      setFailed(true);
    } finally {
      creating.current = false;
    }
  }, [projectId, captureSessionId, ttlSeconds]);

  useEffect(() => {
    void create();
  }, [create]);

  // --- authoritative state -------------------------------------------------

  useEffect(() => {
    if (!pairing || (status && TERMINAL.includes(status))) return undefined;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const next: PairingState = await getPhonePairing(pairing.id);
        if (cancelled) return;
        setStatus(next.status);
        setDeviceLabel(next.device_label ?? null);
      } catch {
        /* A dropped poll is not a state change; the next tick tries again. */
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pairing, status]);

  // --- countdown, which is presentation only -------------------------------

  useEffect(() => {
    if (!pairing || status !== "waiting") {
      setRemaining(null);
      return undefined;
    }
    const tick = () => setRemaining(secondsLeft(pairing.expires_at, Date.now()));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [pairing, status]);

  useEffect(() => {
    // Reaching zero does not expire anything - it asks the server, which is
    // the only clock that decides. A client that expired the pairing itself
    // would disagree with the phone the moment the two clocks drifted.
    if (remaining === 0 && pairing && status === "waiting") {
      void getPhonePairing(pairing.id)
        .then((next) => setStatus(next.status))
        .catch(() => undefined);
    }
  }, [remaining, pairing, status]);

  // --- dismissal -----------------------------------------------------------

  useDialogFocus(dialogRef, { onEscape: onClose });

  const disconnect = useCallback(async () => {
    if (!pairing) return;
    try {
      const next = await revokePhonePairing(pairing.id);
      setStatus(next.status);
    } catch {
      setFailed(true);
    }
  }, [pairing]);

  // --- rendering -----------------------------------------------------------

  const showQr = Boolean(pairing) && status === "waiting" && !failed;

  return (
    <div className="pairing-backdrop" role="presentation" onClick={onClose}>
      <div
        className="pairing-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pairing-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="pairing-title">{PAIRING_TERMS.dialogTitle}</h2>

        {failed && (
          <div className="pairing-state">
            <p className="pairing-badge pairing-badge--warn">
              <span aria-hidden="true">!</span> {PAIRING_TERMS.failed}
            </p>
            <Button onClick={() => void create()}>{PAIRING_TERMS.retry}</Button>
          </div>
        )}

        {showQr && pairing && (
          <div className="pairing-state">
            <div className="pairing-qr">
              {/*
                The only place the secret is used. It goes from state straight
                into the SVG encoder; it is never placed in the DOM as text and
                never written anywhere else.
              */}
              <QRCodeSVG value={pairing.pairing_url} size={224} level="M" marginSize={2} />
            </div>
            <p>{PAIRING_TERMS.scanInstruction}</p>
            <p className="pairing-muted">{PAIRING_TERMS.expiryNotice}</p>

            {/* Changing it mints a new code, because a lifetime cannot be
                extended on a credential that is already out there. */}
            <label className="pairing-ttl">
              <span>Kodun geçerlilik süresi</span>
              <select
                value={ttlSeconds}
                onChange={(event) => setTtlSeconds(Number(event.target.value))}
              >
                <option value={60}>1 dakika</option>
                <option value={300}>5 dakika</option>
                <option value={600}>10 dakika</option>
                <option value={1800}>30 dakika</option>
              </select>
            </label>
            <p className="pairing-status" aria-live="polite">
              <span className="pairing-spinner" aria-hidden="true" />
              {PAIRING_TERMS.waiting}
              {remaining !== null && (
                <span className="pairing-countdown">{formatCountdown(remaining)}</span>
              )}
            </p>
            <p className="pairing-muted pairing-small">{PAIRING_TERMS.shareWarning}</p>
            <p className="pairing-muted pairing-small">{PAIRING_TERMS.sameNetwork}</p>
          </div>
        )}

        {status === "connected" && (
          <div className="pairing-state">
            {/* The QR is gone: it is spent, and leaving it up would keep
                showing bearer material that no longer means anything. */}
            <p className="pairing-badge pairing-badge--ok">
              <span aria-hidden="true">✓</span> {PAIRING_TERMS.connected}
            </p>
            <dl className="pairing-facts">
              <dt>{PAIRING_TERMS.deviceLabel}</dt>
              <dd>{deviceLabel ?? "Mobil tarayıcı"}</dd>
              <dt>{PAIRING_TERMS.connectionLabel}</dt>
              <dd>{PAIRING_TERMS.connectionReady}</dd>
            </dl>
            <p className="pairing-muted">{PAIRING_TERMS.cameraRequiresHttps}</p>
            <Button onClick={() => void disconnect()}>{PAIRING_TERMS.disconnect}</Button>
          </div>
        )}

        {status === "expired" && !failed && (
          <div className="pairing-state">
            <p className="pairing-badge pairing-badge--warn">
              <span aria-hidden="true">!</span> {PAIRING_TERMS.expired}
            </p>
            <Button onClick={() => void create()}>{PAIRING_TERMS.newCode}</Button>
          </div>
        )}

        {status === "revoked" && !failed && (
          <div className="pairing-state">
            <p className="pairing-badge">
              <span aria-hidden="true">•</span> {PAIRING_TERMS.revoked}
            </p>
            <Button onClick={() => void create()}>{PAIRING_TERMS.newCode}</Button>
          </div>
        )}

        <div className="pairing-actions">
          <Button variant="ghost" onClick={onClose}>
            {PAIRING_TERMS.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
}
